// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import type { JSONSchema } from '../types';

import { stableStringify } from './stableStringify';

/**
 * Tracks the JSON path to arrays that have uniqueItems: true.
 * Used to apply uniqueness validation after json-schema-to-zod generates the base schema.
 */
type UniqueItemsPath = string[];

/**
 * Recursively walks a JSON Schema to find all arrays with uniqueItems: true
 * and record their paths. Used to apply uniqueness validation via superRefine.
 */
function findUniqueItemsPaths(schema: JSONSchema, currentPath: string[] = []): UniqueItemsPath[] {
  const paths: UniqueItemsPath[] = [];

  function walk(s: JSONSchema, path: string[]): void {
    if (s.type === 'array') {
      if (s.uniqueItems === true) {
        paths.push([...path]);
      }
      if (s.items) {
        walk(s.items as JSONSchema, [...path, 'items']);
      }
    } else if (s.type === 'object' && s.properties) {
      for (const [key, propSchema] of Object.entries(s.properties)) {
        walk(propSchema as JSONSchema, [...path, 'properties', key]);
      }
    }
  }

  walk(schema, currentPath);
  return paths;
}

/**
 * Validates that all items in an array are unique using deep value comparison.
 * Uses stableStringify to handle objects with different key orders.
 */
function validateUniqueItems(items: unknown[]): boolean {
  const seen = new Set<string>();
  for (const item of items) {
    const key = stableStringify(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }
  return true;
}

/**
 * Recursively navigates a schema path and validates uniqueItems at every matching location.
 *
 * Schema paths contain 'items' and 'properties' segments that don't map 1:1 to data:
 * - 'properties' is skipped (it's just a schema grouping keyword)
 * - 'items' means "fan out across every element of the current array"
 *
 * For example, path ['items', 'properties', 'tags'] on [{tags:['a','a']}]:
 *   - 'items'      → iterate each element of the outer array
 *   - 'properties' → skip
 *   - 'tags'       → navigate into element.tags
 *   - empty path   → validate uniqueness of element.tags
 */
function validateUniquenessAtPath(
  data: unknown,
  remainingPath: string[],
  accessPath: Array<string | number>,
  ctx: z.RefinementCtx,
): void {
  if (remainingPath.length === 0) {
    if (Array.isArray(data) && !validateUniqueItems(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Array items must be unique',
        path: accessPath,
      });
    }
    return;
  }

  const [segment, ...rest] = remainingPath;

  if (segment === 'properties') {
    validateUniquenessAtPath(data, rest, accessPath, ctx);
  } else if (segment === 'items') {
    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        validateUniquenessAtPath(item, rest, [...accessPath, index], ctx);
      });
    }
  } else if (data !== null && data !== undefined && typeof data === 'object' && !Array.isArray(data)) {
    const next = (data as Record<string, unknown>)[segment];
    validateUniquenessAtPath(next, rest, [...accessPath, segment], ctx);
  }
}

/**
 * Applies uniqueItems validation to the appropriate paths in a Zod schema.
 *
 * Since we can't easily traverse and modify nested Zod schemas, we apply
 * a top-level superRefine that validates uniqueness at each path.
 */
function applyUniqueItemsValidation(zodSchema: z.ZodType, uniqueItemsPaths: UniqueItemsPath[]): z.ZodType {
  if (uniqueItemsPaths.length === 0) {
    return zodSchema;
  }

  return zodSchema.superRefine((data, ctx) => {
    for (const path of uniqueItemsPaths) {
      validateUniquenessAtPath(data, path, [], ctx);
    }
  });
}

/**
 * Builds a Zod schema directly from a JSON Schema without code generation or eval.
 *
 * When the codebase migrates to Zod v4, this can be replaced with z.fromJSONSchema().
 */
function buildZodSchema(schema: JSONSchema): z.ZodType {
  switch (schema.type) {
    case 'number':
      return buildNumberSchema(schema, false);
    case 'integer':
      return buildNumberSchema(schema, true);
    case 'string':
      return buildStringSchema(schema);
    case 'boolean':
      return z.boolean();
    case 'object':
      return buildObjectSchema(schema);
    case 'array':
      return buildArraySchema(schema);
    default:
      return z.unknown();
  }
}

function buildNumberSchema(schema: JSONSchema, integer: boolean): z.ZodNumber {
  let s = integer ? z.number().int() : z.number();
  if (typeof schema.minimum === 'number') {
    s = s.min(schema.minimum);
  }
  if (typeof schema.maximum === 'number') {
    s = s.max(schema.maximum);
  }
  if (typeof schema.exclusiveMinimum === 'number') {
    s = s.gt(schema.exclusiveMinimum);
  }
  if (typeof schema.exclusiveMaximum === 'number') {
    s = s.lt(schema.exclusiveMaximum);
  }
  if (typeof schema.multipleOf === 'number') {
    s = s.multipleOf(schema.multipleOf);
  }
  return s;
}

function buildStringSchema(schema: JSONSchema): z.ZodType {
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    const stringValues = schema.enum.filter((v): v is string => typeof v === 'string');
    if (stringValues.length > 0) {
      return z.enum(stringValues as [string, ...string[]]);
    }
  }
  let s = z.string();
  if (typeof schema.minLength === 'number') {
    s = s.min(schema.minLength);
  }
  if (typeof schema.maxLength === 'number') {
    s = s.max(schema.maxLength);
  }
  return s;
}

function buildObjectSchema(schema: JSONSchema): z.ZodObject<z.ZodRawShape> {
  const properties = schema.properties as Record<string, JSONSchema> | undefined;
  const required = new Set<string>(Array.isArray(schema.required) ? schema.required : []);

  const shape: z.ZodRawShape = {};
  for (const [key, propSchema] of Object.entries(properties ?? {})) {
    const fieldSchema = buildZodSchema(propSchema);
    shape[key] = required.has(key) ? fieldSchema : fieldSchema.optional();
  }
  return z.object(shape);
}

function buildArraySchema(schema: JSONSchema): z.ZodArray<z.ZodType> {
  const itemSchema = schema.items ? buildZodSchema(schema.items as JSONSchema) : z.unknown();
  let s = z.array(itemSchema);
  if (typeof schema.minItems === 'number') {
    s = s.min(schema.minItems);
  }
  if (typeof schema.maxItems === 'number') {
    s = s.max(schema.maxItems);
  }
  return s;
}

/**
 * Converts a JSON Schema to a Zod schema.
 *
 * Builds the Zod schema directly via the Zod API — no code generation or eval.
 * When the codebase migrates to Zod v4, this can be replaced with z.fromJSONSchema().
 *
 * Handles `uniqueItems: true` by adding custom validation via .superRefine(),
 * since Zod doesn't have a built-in .unique() method.
 *
 * @param schema - A valid JSON Schema object
 * @returns A Zod schema that validates according to the JSON Schema
 */
export function schemaToZod(schema: JSONSchema): z.ZodType {
  const uniqueItemsPaths = findUniqueItemsPaths(schema);
  const baseSchema = buildZodSchema(schema);
  return applyUniqueItemsValidation(baseSchema, uniqueItemsPaths);
}
